import { types } from "mobx-state-tree";
import {flow} from "mobx";
import {api, v3Endpoint} from "../api";
import { $spinner } from "./spinner";
import { DEFAULT_FAST_ORDER_QUERY, FAST_ORDER_SORT_FIELDS } from "./fastOrderLinksQuery";

export const $fastOrderLinks = types
  .model({
    discounts: types.optional(types.array(types.frozen()), []),
    loading: types.optional(types.boolean, false),
    total: types.optional(types.number, 0),
    currentPage: types.optional(types.number, DEFAULT_FAST_ORDER_QUERY.currentPage),
    perPage: types.optional(types.number, DEFAULT_FAST_ORDER_QUERY.perPage),
    onlyActive: types.optional(types.boolean, DEFAULT_FAST_ORDER_QUERY.onlyActive),
    sortBy: types.optional(types.enumeration(["", ...FAST_ORDER_SORT_FIELDS]), DEFAULT_FAST_ORDER_QUERY.sortBy),
    sortOrder: types.optional(types.enumeration(["asc", "desc"]), DEFAULT_FAST_ORDER_QUERY.sortOrder),
  })
  .actions((self) => {
    let latestRequestId = 0;

    return {
      setLoading(loading) {
        self.loading = loading;
      },
      setDiscounts(discounts) {
        self.discounts = discounts;
      },
      setTotal(total) {
        self.total = total;
      },
      setQuery({ currentPage, perPage, onlyActive, sortBy, sortOrder }) {
        self.currentPage = currentPage;
        self.perPage = perPage;
        self.onlyActive = onlyActive;
        self.sortBy = sortBy;
        self.sortOrder = sortOrder;
      },

      // CRUD операции для скидок
      fetchDiscounts: flow(function* () {
        const requestId = ++latestRequestId;
        self.setLoading(true);
        $spinner.show();
        try {
          const response = yield api(v3Endpoint)
            .get('/fast-order-discounts', {
              params: {
                page: self.currentPage,
                per_page: self.perPage,
                ...(self.onlyActive && { only_active: 1 }),
                ...(self.sortBy && {
                  sort_by: self.sortBy,
                  sort_order: self.sortOrder,
                }),
              },
            })
            .then((res) => res.data);

          // Быстрые переходы по URL не должны заменять список устаревшим ответом.
          if (requestId !== latestRequestId) return;

          if (response && response.data && Array.isArray(response.data)) {
            self.setDiscounts(response.data);
            self.setTotal(response.meta?.total ?? response.total ?? response.data.length);
          } else if (Array.isArray(response)) {
            self.setDiscounts(response);
            self.setTotal(response.length);
          } else {
            console.warn('Неожиданная структура ответа API:', response);
            self.setDiscounts([]);
            self.setTotal(0);
          }
        } catch (error) {
          if (requestId !== latestRequestId) return;
          console.error('Ошибка при загрузке скидок:', error);
          self.setDiscounts([]);
          self.setTotal(0);
        } finally {
          if (requestId === latestRequestId) {
            self.setLoading(false);
            $spinner.hide();
          }
        }
      }),

      createDiscount: flow(function* (data) {
        try {
          $spinner.show();
          const response = yield api(v3Endpoint)
            .post('/fast-order-discounts', data)
            .then((res) => res.data);

          yield self.fetchDiscounts();
          return response;
        } catch (error) {
          console.error('Ошибка при создании скидки:', error);
          throw error;
        } finally {
          $spinner.hide();
        }
      }),

      updateDiscount: flow(function* (id, data) {
        try {
          $spinner.show();
          const response = yield api(v3Endpoint)
            .put(`/fast-order-discounts/${id}`, data)
            .then((res) => res.data);

          yield self.fetchDiscounts();
          return response;
        } catch (error) {
          console.error('Ошибка при обновлении скидки:', error);
          throw error;
        } finally {
          $spinner.hide();
        }
      }),

      deleteDiscount: flow(function* (id) {
        try {
          $spinner.show();
          yield api(v3Endpoint)
            .delete(`/fast-order-discounts/${id}`);

          yield self.fetchDiscounts();
        } catch (error) {
          console.error('Ошибка при удалении скидки:', error);
          throw error;
        } finally {
          $spinner.hide();
        }
      }),

      getDiscount: flow(function* (id) {
        try {
          const response = yield api(v3Endpoint)
            .get(`/fast-order-discounts/${id}`)
            .then((res) => res.data);

          return response;
        } catch (error) {
          console.error('Ошибка при получении скидки:', error);
          throw error;
        }
      }),
    };
  })
  .create();
